/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { Component, Input, inject, ChangeDetectionStrategy } from '@angular/core'
import { TransactionType } from '../../enums/transaction-type'
import { Store } from '@ngxs/store'
import { SignupService } from '../../services/signup/signup.service'
import { WINDOW } from '../../services/utils'
import { ConfigService } from '../../services/config/config.service'
import { TranslateService } from '@ngx-translate/core'
import { ConfirmationSelectors } from '../../state/confirmation/confirmation.selectors'
import { Observable } from 'rxjs'
import { Confirmation } from '../../state/confirmation/confirmation.actions'
import RequestOTP = Confirmation.RequestOTP
import { LegalCondition } from '../../models/legal-conditions'

@Component({
  selector: 'myprofile-signup-ui-request-sms-code',
  templateUrl: './request-sms-code.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class RequestSmsCodeComponent {
  private _window = inject<Window>(WINDOW)
  private _signupService = inject(SignupService)
  private readonly store = inject(Store)
  private _translateService = inject(TranslateService)
  private _configService = inject(ConfigService)

  @Input() mobile!: string
  @Input() currentPageName!: string
  @Input() legalConditions!: LegalCondition[]

  public readonly transactionType$: Observable<TransactionType> = inject(Store).select(ConfirmationSelectors.transactionType)
  public readonly otpRequested$: Observable<boolean> = inject(Store).select(ConfirmationSelectors.otpRequested)
  public readonly otpRequestedBlocked$: Observable<boolean> = inject(Store).select(ConfirmationSelectors.otpRequestBlocked)

  public readonly TransactionType = TransactionType
  public openBanner = false

  constructor() {
    this.otpRequestedBlocked$.subscribe((blocked) => {
      if (blocked) {
        this.openBanner = true
      }
    })
  }

  public closeBanner() {
    this.openBanner = false
  }

  get isAddVehicleLeanActivation() {
    return (
      this._signupService.isLeanActivationCountry &&
      [
        TransactionType.ADD_CONNECT_VEHICLE,
        TransactionType.ADD_NON_CONNECT_VEHICLE,
        TransactionType.SIMPLIFIED_ADD_CONNECT_VEHICLE,
        TransactionType.SIMPLIFIED_ADD_NON_CONNECT_VEHICLE,
      ].includes(this._signupService.transactionType)
    )
  }

  get leanActivationText() {
    const translated = this._translateService.instant('signup.activateAccount.addVehicle.leanActivation.legalText')
    return translated
      .replace(/{{tacLink}}/, this.legalConditions?.find((lc) => lc.type === 'TAC')?.linkPDF ?? '') // replace TAC linkExt does not exist
      .replace(/{{touLink}}/, this.legalConditions?.find((lc) => lc.type === 'TOU')?.linkPDF ?? '') // replace TOU linkExt does not exist
      .replace(/{{dpgLink}}/, this.legalConditions?.find((lc) => lc.type === 'DPG')?.linkExt ?? '')
  }

  goToManualAddVehicleProcess() {
    const { vehicle } = this._signupService.transactionData
    this._window.location.href = this._configService.getExternalUrl('portalAddVehicle') + '/' + vehicle.vin
  }

  public onRequestOTP(resend = false) {
    this.store.dispatch(new RequestOTP(resend))
  }
}
